import create, { UseStore } from 'zustand';

type Validator<T, U> = (value: T) => U;

export type Validation<T = any, U = string> = {
  error?: U;
  valid?: Validator<T, U>;
  defaultValue?: T;
  sanitize?: (value: T) => T;
  onChange?: (value: T) => void;
  _onChange?: (value: T) => void;
};

export type Value<T> = Validation<T> & {
  value: T;
  setValue(v: T): void;
  reset(v?: T): void;
};

export type ValueApi<T = any> = UseStore<Value<T>>;

export function valueApi<T = any>(props?: Validation): ValueApi<T> {
  const { valid, defaultValue, sanitize, onChange } = { ...props };

  const onValueChange = (newValue: T, existing: T) => {
    if (onChange && newValue !== existing) {
      onChange(newValue);
    }
  };

  return create<Value<T>>((set, get) => ({
    value: defaultValue,
    setValue: async (value) => {
      set({ error: null });

      value = sanitize ? sanitize(value) : value;
      set({ value });

      if (valid) {
        const error = await valid(value);

        if (error) {
          set({ error });
          return;
        }
      }

      onValueChange(value, get().value);
    },
    reset: (v) => {
      const value = v || defaultValue;

      onValueChange(value, get().value);

      set({ value, defaultValue: value, error: null });
    },
    defaultValue,
    error: null,
    valid,
  }));
}

interface ValueIndexArgs<T = any> extends Validation<T> {
  list: T[];
  defaultValue?: T;
  equals?: (a: T, b: T) => boolean;
  min?: number;
}

const defaultValueIndexArgs: Partial<ValueIndexArgs> = {
  equals: (a, b) => a === b,
  min: -1,
};

interface ValueIndex<T> {
  value: UseStore<Value<T>>;
  index: UseStore<Value<number>>;
}

export function valueIndex<T = any>(args: ValueIndexArgs<T>): ValueIndex<T> {
  const { list, defaultValue, equals, min } = {
    ...defaultValueIndexArgs,
    ...args,
  };

  let currentIndex = -1;
  let currentValue = defaultValue;

  const valueIndex = (b: T) => {
    const f = list.filter((a) => equals(a, b));
    return f.length ? list.indexOf(f[0]) : min;
  };

  if (defaultValue) {
    currentIndex = valueIndex(defaultValue);
  }

  const value = valueApi<T>({
    ...args,
    ...{
      defaultValue,
      onChange: (v) => {
        if (currentValue !== v) {
          index.getState().setValue(valueIndex(v));
        }
        if (args.onChange) {
          args.onChange(v);
        }
      },
    },
  });

  const index = valueApi<number>({
    defaultValue: currentIndex,
    sanitize: (v) => Math.max(min, Math.min(v, list.length - 1)),
    onChange: (i) => {
      currentIndex = i;
      currentValue = list[i];
      value.getState().setValue(currentValue);
    },
  });

  return { value, index };
}
